import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { describeApiError } from '@/lib/api/errors';
import {
  DEVIATION_LEGAL_TRANSITIONS,
  DEVIATION_SEVERITIES,
  DEVIATION_TYPES,
  useCreateDeviationMutation,
  useTransitionDeviationMutation,
  type DeviationSummary,
} from '@/lib/api/deviations';
import { useEdlListQuery } from '@/lib/api/deliverables';
import { useEvidenceLinksBySourceQuery } from '@/lib/api/evidence-links';
import { deviationStatusLabel, deviationTypeLabel } from '@/lib/contract-labels';
import { cn } from '@/lib/utils/cn';

/** The forms' field labels, so a validation error names the field as the form does (C2-31). */
const CREATE_FIELDS: Record<string, string> = {
  title: 'Title',
  severity: 'Severity',
  deviation_type: 'Type',
  deliverable_entry_id: 'Linked deliverable',
  framework_definition_id: 'Deviation framework',
};
const TRANSITION_FIELDS: Record<string, string> = {
  to_status: 'Status',
  waiver_reason: 'Reason for waiving',
  evidence_link_ids: 'Resolution evidence',
  note: 'Note',
};

/** Mirrors the API's MIN_WAIVER_REASON_CHARS: one sentence. */
const MIN_WAIVER_REASON_CHARS = 10;

/**
 * Deviation lifecycle modals (Tier 2 — E06-F06).
 *
 * Create: title + severity + type + optional deliverable scope.
 * Transition: legal next states per the API's state machine —
 *   - waive requires a reason (min 10 chars, lead-only server-side); it is
 *     stored with the deviation and shown in its history (C2-19)
 *   - resolve requires at least one evidence link on the deviation entry
 */

const inputCls =
  'mt-1 w-full rounded-card border border-ink-200 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300';

function ModalShell({
  title,
  subtitle,
  open,
  onOpenChange,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[min(520px,94vw)] max-h-[90vh] -translate-x-1/2 -translate-y-1/2',
            'grid grid-rows-[auto,1fr,auto] overflow-hidden rounded-modal bg-paper-0 shadow-modal',
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-ink-100 bg-paper-50 px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="text-h2 font-semibold text-ink-900">{title}</Dialog.Title>
              {subtitle ? <p className="mt-1 truncate text-meta text-ink-500">{subtitle}</p> : null}
            </div>
            <Dialog.Close
              aria-label="Close"
              className="inline-flex h-8 w-8 items-center justify-center rounded-card text-ink-500 hover:bg-paper-100 hover:text-ink-900"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Dialog.Close>
          </div>
          <div className="space-y-4 overflow-y-auto px-5 py-4">{children}</div>
          <div className="flex items-center justify-end gap-2 border-t border-ink-100 bg-paper-50 px-5 py-3">
            {footer}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function CreateDeviationModal({
  engagementId,
  open,
  onOpenChange,
}: {
  engagementId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState<string>('Medium');
  const [deviationType, setDeviationType] = useState<string>('quality');
  const [deliverableId, setDeliverableId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateDeviationMutation(engagementId);
  const edl = useEdlListQuery(engagementId);

  function submit() {
    setError(null);
    create.mutate(
      {
        body: {
          engagement_id: engagementId,
          title: title.trim(),
          severity,
          deviation_type: deviationType,
          deliverable_entry_id: deliverableId || null,
        },
        idempotencyKey: crypto.randomUUID(),
      },
      {
        onSuccess: () => {
          setTitle('');
          onOpenChange(false);
        },
        onError: (e) => setError(describeApiError(e, CREATE_FIELDS, 'Create failed.')),
      },
    );
  }

  return (
    <ModalShell
      title="New deviation"
      subtitle="Records a gap between the contracted baseline and observed delivery."
      open={open}
      onOpenChange={onOpenChange}
      footer={
        <>
          <Dialog.Close asChild>
            <Button type="button" variant="ghost" size="sm">
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            type="button"
            size="sm"
            disabled={title.trim().length === 0 || create.isPending}
            onClick={submit}
          >
            {create.isPending ? <Spinner /> : null}
            Create deviation
          </Button>
        </>
      }
    >
      <label className="block text-meta font-medium text-ink-700">
        Title
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder="e.g. UAT exit report missed committed date"
          className={inputCls}
        />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-meta font-medium text-ink-700">
          Severity
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className={inputCls}
          >
            {DEVIATION_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-meta font-medium text-ink-700">
          Type
          <select
            value={deviationType}
            onChange={(e) => setDeviationType(e.target.value)}
            className={inputCls}
          >
            {DEVIATION_TYPES.map((t) => (
              <option key={t} value={t}>
                {deviationTypeLabel(t)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="block text-meta font-medium text-ink-700">
        Linked deliverable (optional)
        <select
          value={deliverableId}
          onChange={(e) => setDeliverableId(e.target.value)}
          className={inputCls}
        >
          <option value="">— none —</option>
          {(edl.data?.entries ?? []).map((d) => (
            <option key={d.register_entry_id} value={d.register_entry_id}>
              {d.sow_ref ? `${d.sow_ref} · ${d.title}` : d.title}
            </option>
          ))}
        </select>
      </label>
      {error ? <p className="text-meta font-medium text-cite-broken">{error}</p> : null}
    </ModalShell>
  );
}

export function TransitionDeviationModal({
  engagementId,
  deviation,
  open,
  onOpenChange,
}: {
  engagementId: string;
  deviation: DeviationSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [toStatus, setToStatus] = useState<string>('');
  const [note, setNote] = useState('');
  const [waiverReason, setWaiverReason] = useState('');
  const [pickedEvidence, setPickedEvidence] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const transition = useTransitionDeviationMutation(engagementId);
  // A deviation IS a register entry — its evidence links are the resolve
  // proof the API requires.
  const evidence = useEvidenceLinksBySourceQuery(
    engagementId,
    'register_entry',
    deviation.deviation_entry_id,
    { enabled: open },
  );

  const nextStatuses = DEVIATION_LEGAL_TRANSITIONS[deviation.status] ?? [];
  const evidenceItems = evidence.data?.items ?? [];
  const waiveBlocked =
    toStatus === 'waived' && waiverReason.trim().length < MIN_WAIVER_REASON_CHARS;
  const resolveBlocked = toStatus === 'resolved' && pickedEvidence.size === 0;

  function submit() {
    setError(null);
    transition.mutate(
      {
        deviationEntryId: deviation.deviation_entry_id,
        body: {
          to_status: toStatus,
          note: note.trim() ? note.trim() : null,
          waiver_reason: toStatus === 'waived' ? waiverReason.trim() : null,
          evidence_link_ids: toStatus === 'resolved' ? Array.from(pickedEvidence) : null,
        },
        idempotencyKey: crypto.randomUUID(),
      },
      {
        onSuccess: () => onOpenChange(false),
        onError: (e) => setError(describeApiError(e, TRANSITION_FIELDS, 'Transition failed.')),
      },
    );
  }

  return (
    <ModalShell
      title="Update deviation status"
      subtitle={deviation.title}
      open={open}
      onOpenChange={onOpenChange}
      footer={
        <>
          <Dialog.Close asChild>
            <Button type="button" variant="ghost" size="sm">
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            type="button"
            size="sm"
            disabled={!toStatus || waiveBlocked || resolveBlocked || transition.isPending}
            onClick={submit}
          >
            {transition.isPending ? <Spinner /> : null}
            Apply
          </Button>
        </>
      }
    >
      {nextStatuses.length === 0 ? (
        <p className="rounded-card border border-ink-100 bg-paper-50 p-3 text-meta text-ink-700">
          <strong>{deviationStatusLabel(deviation.status)}</strong> is final — no further status
          changes.
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {nextStatuses.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setToStatus(s)}
              className={cn(
                'rounded-card border px-3 py-2 text-left text-body-sm font-medium',
                toStatus === s
                  ? 'border-accent-amber-600 bg-accent-amber-50 text-ink-900'
                  : 'border-ink-200 bg-paper-0 text-ink-700 hover:bg-paper-50',
              )}
            >
              {deviationStatusLabel(s)}
            </button>
          ))}
        </div>
      )}

      {toStatus === 'waived' ? (
        <label className="block text-meta font-medium text-ink-700">
          Reason for waiving <span className="text-posture-critical">· required</span>
          <span className="block font-normal text-ink-500">
            One sentence, kept with the deviation and shown in its history. Engagement lead only.
          </span>
          <textarea
            value={waiverReason}
            onChange={(e) => setWaiverReason(e.target.value)}
            rows={3}
            placeholder="e.g. The client accepted the delay at the 2 October steering meeting."
            className={inputCls}
          />
          {waiveBlocked && waiverReason.length > 0 ? (
            <span className="mt-1 block font-medium text-cite-broken">
              {MIN_WAIVER_REASON_CHARS - waiverReason.trim().length} more characters needed.
            </span>
          ) : null}
        </label>
      ) : null}

      {toStatus === 'resolved' ? (
        <div className="space-y-2">
          <p className="text-meta font-medium text-ink-700">
            Resolution evidence (pick at least one link attached to this deviation)
          </p>
          {evidence.isLoading ? (
            <Spinner />
          ) : evidenceItems.length === 0 ? (
            <p className="rounded-card border border-accent-amber-100 bg-accent-amber-50 p-3 text-meta text-accent-amber-700">
              This deviation has no evidence links yet. Attach evidence from the document viewer
              (link-to-register) first — resolution requires documented proof.
            </p>
          ) : (
            <ul className="space-y-1">
              {evidenceItems.map((ev) => (
                <li key={ev.id}>
                  <label className="flex items-start gap-2 rounded-card border border-ink-100 bg-paper-0 p-2 text-meta text-ink-700">
                    <input
                      type="checkbox"
                      checked={pickedEvidence.has(ev.id)}
                      onChange={(e) => {
                        setPickedEvidence((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(ev.id);
                          else next.delete(ev.id);
                          return next;
                        });
                      }}
                      className="mt-0.5 h-3.5 w-3.5 rounded-sm border-ink-300"
                    />
                    <span>
                      {ev.document_title}
                      {ev.page != null ? ` · p.${ev.page}` : ''}
                      {ev.section_path ? ` · ${ev.section_path}` : ''}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {toStatus && toStatus !== 'waived' ? (
        <label className="block text-meta font-medium text-ink-700">
          Note (optional)
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className={inputCls}
          />
        </label>
      ) : null}

      {error ? <p className="text-meta font-medium text-cite-broken">{error}</p> : null}
    </ModalShell>
  );
}
